import * as XLSX from "xlsx";

/** Build a single error message string from an error object from the API. */
/** Field-keyed messages from dry-run / transformer rows (`errors` or `_errors`). */
export function getNestedFieldErrorsObject(
  error: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const fromErrors = error.errors as Record<string, unknown> | undefined;
  const fromUnderscore = error._errors as Record<string, unknown> | undefined;
  if (fromErrors && fromUnderscore) {
    return { ...fromUnderscore, ...fromErrors };
  }
  return fromErrors ?? fromUnderscore;
}

export function getErrorMessage(error: {
  _error?: unknown;
  errors?: unknown;
  _errors?: unknown;
  error?: unknown;
}): string {
  if (error._error != null && String(error._error).trim() !== "") {
    return String(error._error);
  }
  if (error.error != null && String(error.error).trim() !== "") {
    return String(error.error);
  }
  if (error.errors != null && typeof error.errors === "object") {
    return Object.entries(error.errors as Record<string, unknown>)
      .map(([field, message]) => `${field}: ${String(message)}`)
      .join("; ");
  }
  const merged = getNestedFieldErrorsObject(error as Record<string, unknown>);
  if (merged && typeof merged === "object") {
    return Object.entries(merged)
      .map(([field, message]) => `${field}: ${String(message)}`)
      .join("; ");
  }
  return String(error.errors ?? "Unknown error");
}

/** Keys we exclude from "input data" when building export rows. */
const ERROR_EXPORT_SKIP_KEYS = new Set([
  "row_index",
  "_error",
  "_errors",
  "errors",
  "error",
  "data",
]);

/** How to build the "visible columns" error export (match on-screen error UI). */
export type ErrorsExportVisibleStrategy =
  | { kind: "error-table"; canonicalFieldOrder: string[] }
  | { kind: "jurisdiction"; canonicalFieldOrder: string[] }
  | { kind: "simple" };

export type ErrorExportColumnMode = "visible" | "all";

/**
 * Spreadsheet line number for import/dry-run errors: row 1 is the header row;
 * the first data row is line 2. Prefer `_original_row_index` from the file reader,
 * then `row_index` (DataTransformer uses the same semantics when set).
 */
export function spreadsheetLineNumberFromImportError(
  err: Record<string, unknown>,
  errorListIndex: number,
): number {
  const orig = err._original_row_index;
  if (typeof orig === "number" && Number.isFinite(orig)) {
    return orig;
  }
  if (typeof err.row_index === "number" && Number.isFinite(err.row_index)) {
    return err.row_index;
  }
  const row = (err as { row?: unknown }).row;
  if (typeof row === "number" && Number.isFinite(row)) {
    return row;
  }
  // No metadata: assume errors are listed in sheet order from the first data row.
  return errorListIndex + 2;
}

function errorRowNumberString(
  err: Record<string, unknown>,
  index: number,
): string {
  return String(spreadsheetLineNumberFromImportError(err, index));
}

function cellValueForErrorField(
  err: Record<string, unknown>,
  field: string,
): string {
  if (field === "_error") {
    const v = err._error ?? err.error;
    return v === null || v === undefined ? "" : String(v);
  }
  const nested = getNestedFieldErrorsObject(err);
  const v = nested?.[field];
  if (v === null || v === undefined) {
    return "";
  }
  return typeof v === "object" ? JSON.stringify(v) : String(v);
}

function collectErrorFieldsForLicense(
  errors: Array<Record<string, unknown>>,
): Set<string> {
  const errorFields = new Set<string>();
  for (const err of errors) {
    const nested = getNestedFieldErrorsObject(err);
    if (nested && typeof nested === "object") {
      for (const field of Object.keys(nested)) {
        errorFields.add(field);
      }
    }
    if (err._error != null && String(err._error).trim() !== "") {
      errorFields.add("_error");
    }
    if (err.error != null && String(err.error).trim() !== "") {
      errorFields.add("_error");
    }
  }
  return errorFields;
}

function collectErrorFieldsForJurisdiction(
  errors: Array<Record<string, unknown>>,
): Set<string> {
  const errorFields = new Set<string>();
  for (const err of errors) {
    for (const field of Object.keys(
      (err.errors || {}) as Record<string, unknown>,
    )) {
      errorFields.add(field);
    }
  }
  return errorFields;
}

function getShownFieldsErrorTable(
  errorFields: Set<string>,
  canonicalFieldOrder: string[],
): string[] {
  const base =
    errorFields.size > 0
      ? canonicalFieldOrder.filter((f) => f != null && errorFields.has(f))
      : canonicalFieldOrder.filter((f) => f != null);
  if (errorFields.has("_error") && !base.includes("_error")) {
    return [...base, "_error"];
  }
  return base;
}

/**
 * "All" export: each row has row_number, a combined `error` message, and
 * top-level input keys from the error object (not nested structures).
 */
export function getErrorsExportRowsAll(
  errors: Array<Record<string, unknown>>,
): Array<Record<string, string>> {
  const allKeys = new Set<string>(["row_number", "error"]);
  for (const err of errors) {
    for (const key of Object.keys(err)) {
      if (!ERROR_EXPORT_SKIP_KEYS.has(key) && !key.startsWith("_")) {
        allKeys.add(key);
      }
    }
  }
  const dataKeys = [...allKeys]
    .filter((k) => k !== "row_number" && k !== "error")
    .sort();

  return errors.map((err, index) => {
    const row: Record<string, string> = {
      row_number: errorRowNumberString(err, index),
      error: getErrorMessage(err),
    };
    for (const key of dataKeys) {
      const v = err[key];
      row[key] =
        v === null || v === undefined
          ? ""
          : typeof v === "object"
            ? JSON.stringify(v)
            : String(v);
    }
    return row;
  });
}

function getErrorsExportRowsVisible(
  errors: Array<Record<string, unknown>>,
  strategy: ErrorsExportVisibleStrategy,
): Array<Record<string, string>> {
  if (strategy.kind === "simple") {
    return errors.map((err, index) => ({
      row_number: errorRowNumberString(err, index),
      error: getErrorMessage(err),
    }));
  }

  if (strategy.kind === "jurisdiction") {
    const errorFields = collectErrorFieldsForJurisdiction(errors);
    const shownFields = strategy.canonicalFieldOrder.filter((f) =>
      errorFields.has(f),
    );
    return errors.map((err, index) => {
      const row: Record<string, string> = {
        row_number: errorRowNumberString(err, index),
      };
      for (const field of shownFields) {
        const v = (err.errors as Record<string, unknown> | undefined)?.[field];
        row[field] = v == null ? "" : String(v);
      }
      return row;
    });
  }

  // error-table (license / unregistered dry-run style)
  const errorFields = collectErrorFieldsForLicense(errors);
  const shownFields = getShownFieldsErrorTable(
    errorFields,
    strategy.canonicalFieldOrder,
  );
  return errors.map((err, index) => {
    const row: Record<string, string> = {
      row_number: errorRowNumberString(err, index),
    };
    for (const field of shownFields) {
      row[field] = cellValueForErrorField(err, field);
    }
    return row;
  });
}

export type DownloadErrorsOptions = {
  mode: ErrorExportColumnMode;
  /** Required when `mode` is "visible". */
  visible: ErrorsExportVisibleStrategy;
};

/**
 * Build an array of export rows. With no `options`, returns the same as
 * "all" mode (row_number, error, and top-level data keys).
 */
export function getErrorsExportRows(
  errors: Array<Record<string, unknown>>,
  options?: DownloadErrorsOptions,
): Array<Record<string, string>> {
  if (!options || options.mode === "all") {
    return getErrorsExportRowsAll(errors);
  }
  return getErrorsExportRowsVisible(errors, options.visible);
}

/**
 * Download errors as CSV. `filenamePrefix` is used for the file name, e.g. "unregistered-errors".
 * Pass `options` to choose visible vs all columns; omit to export all columns (back-compat).
 */
export function downloadErrorsAsCSV(
  errors: Array<Record<string, unknown>>,
  filenamePrefix: string,
  options?: DownloadErrorsOptions,
): void {
  const rows = options
    ? getErrorsExportRows(errors, options)
    : getErrorsExportRowsAll(errors);
  if (rows.length === 0) return;
  const first = rows[0];
  const headers = first ? Object.keys(first) : [];
  const csvData = [headers, ...rows.map((r) => headers.map((h) => r[h] ?? ""))];
  const csvContent = csvData
    .map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
    )
    .join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `${filenamePrefix}-${new Date().toISOString().split("T")[0]}.csv`,
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Download errors as XLSX. Optional `options` for visible vs all column modes.
 */
export function downloadErrorsAsXLSX(
  errors: Array<Record<string, unknown>>,
  filenamePrefix: string,
  options?: DownloadErrorsOptions,
): void {
  const rows = options
    ? getErrorsExportRows(errors, options)
    : getErrorsExportRowsAll(errors);
  if (rows.length === 0) return;
  const first = rows[0];
  const headers = first ? Object.keys(first) : [];
  const excelData = [
    headers,
    ...rows.map((r) => headers.map((h) => r[h] ?? "")),
  ];
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(excelData);
  XLSX.utils.book_append_sheet(wb, ws, "Errors");
  XLSX.writeFile(
    wb,
    `${filenamePrefix}-${new Date().toISOString().split("T")[0]}.xlsx`,
  );
}
